import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import type {
  BranchesResponse,
  CircleListResponse,
  CircleSummary,
  CreateUserResponse,
  Gender,
  JoinRequest,
} from "@mubin/contracts";
import { api, errorText, send } from "../lib/api";
import { genderLabel, personGenderLabel, roleLabel } from "../lib/labels";
import { useCan, useOrg } from "../lib/org";
import { useMe } from "../lib/session";
import { ErrorNote, Field, Modal, useSubmit } from "./ui";
import { PhoneField } from "./phone-field";
import { isStaff, useChoices } from "./roles";
import { ProgramFields, emptyProgram, programOf } from "./program-fields";

type NewRole = "student" | "teacher" | "supervisor" | "branch_manager";

export interface Credentials {
  name: string;
  login: string;
  password: string;
}

/**
 * Create an account. Students are enrolled in a circle in the same step; staff get a branch-scoped role.
 * Also used to accept a join request (prefilled, closes the request in the same transaction).
 */
export function CreateUserModal({
  open,
  onClose,
  role: fixedRole,
  circle,
  joinRequest,
  prefill,
  onCreated,
  staffOnly,
}: {
  open: boolean;
  onClose: () => void;
  role?: NewRole;
  circle?: CircleSummary;
  joinRequest?: JoinRequest;
  /** Known details of the person (an accepted volunteer). */
  prefill?: { name: string; phone: string; gender: Gender };
  /** After the account is made (e.g. to close the application it came from). */
  onCreated?: (userId: string) => Promise<void>;
  /** Opened from «الإشراف والإدارة»: only staff roles to choose from. */
  staffOnly?: boolean;
}) {
  const qc = useQueryClient();
  const org = useOrg();
  const { data: me } = useMe();
  const can = useCan();
  // The same choices as granting a role: the organization's roles and the built-in ones (ADR 0018).
  const choices = useChoices().filter((c) => (isStaff(c.role) ? can.has("users.roles") : !staffOnly));
  const { busy, error, run, setError } = useSubmit();
  const [done, setDone] = useState<{
    name: string;
    phone: string;
    existing: boolean;
    whatsapp?: CreateUserResponse["whatsapp"];
  } | null>(null);
  const blank = () => ({
    choice: fixedRole ?? (joinRequest ? "student" : ""),
    wide: false,
    name: joinRequest?.name ?? prefill?.name ?? "",
    phone: joinRequest?.phone ?? prefill?.phone ?? "",
    gender: (joinRequest?.gender ?? prefill?.gender ?? circle?.gender ?? "") as Gender | "",
    branchId: joinRequest?.branchId ?? circle?.branchId ?? "",
    circleId: circle?.id ?? "",
  });
  const [form, setForm] = useState(blank);
  // A new student's program: the same choices as signing up in the app (owner decision 2026-11-03).
  const [program, setProgram] = useState(emptyProgram);
  const [planFrom, setPlanFrom] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Riyadh" }),
  );
  const picked = choices.find((c) => c.key === form.choice) ?? {
    key: form.choice,
    label: "",
    role: (form.choice || "student") as NewRole,
    profileId: null,
  };
  const role = picked.role;
  const staff = isStaff(role);
  const orgWide = role === "org_admin" || (staff && form.wide);
  useEffect(() => {
    if (open) {
      setForm(blank());
      setProgram(emptyProgram());
      setDone(null);
      setError(null);
    }
  }, [open]);

  const branches = useQuery({
    queryKey: ["branches", me?.activeRole],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
    enabled: open,
  });
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, "all"],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
    enabled: open && role === "student" && !circle,
  });
  const circleChoices = (circles.data?.circles ?? []).filter(
    (c) =>
      (!form.branchId || c.branchId === form.branchId) &&
      (!c.gender || !form.gender || c.gender === form.gender),
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      if (!org) throw new Error("no organization");
      const isStudent = role === "student";
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        gender: form.gender || undefined,
        organizationId: org.id,
        role,
        ...(picked.profileId && { profileId: picked.profileId }),
        scopeType: isStudent ? "self" : orgWide ? "organization" : "branch",
        scopeId: isStudent ? null : orgWide ? org.id : form.branchId,
        enrollInCircleId: isStudent ? form.circleId || circle?.id || undefined : undefined,
        ...(isStudent && { program: programOf(program) ?? undefined, planFrom }),
        joinRequestId: joinRequest?.id,
      };
      const created = await send<CreateUserResponse>("POST", "/v1/users", body);
      await onCreated?.(created.userId);
      await Promise.all(
        ["users", "circles", "overview", "join-requests"].map((k) => qc.invalidateQueries({ queryKey: [k] })),
      );
      setDone({
        name: body.name,
        phone: body.phone,
        existing: !!created.existing,
        whatsapp: created.whatsapp,
      });
    }, errorText);
  }

  const isStudent = role === "student";
  const valid =
    !!form.choice &&
    form.name.trim().length >= 2 &&
    form.phone.replace(/\D/g, "").length >= 9 &&
    (isStudent ? !!form.circleId && !!programOf(program) : orgWide || !!form.branchId);

  return (
    <Modal
      title={
        joinRequest
          ? "قبول طلب الانضمام"
          : done
            ? done.existing
              ? "أُضيف الدور للحساب"
              : "تم إنشاء الحساب"
            : "حساب جديد"
      }
      open={open}
      onClose={onClose}
      wide
    >
      {done ? (
        done.existing ? (
          <div className="form">
            <p>
              الرقم <bdi dir="ltr">{done.phone}</bdi> له حساب في المنصة، فأُضيف له دور «
              {picked.profileId ? picked.label : roleLabel[role]}» على الحساب نفسه. يدخل به كما كان يدخل،
              ويختار الدور من أعلى الصفحة أو من التطبيق.
            </p>
            {done.whatsapp && (
              <p className="notice small" role="status">
                {done.whatsapp.sent
                  ? "أُرسلت له رسالة واتساب من رقم الجمعية برابط المنصة وطريقة الدخول."
                  : `لم تُرسل رسالة الواتساب (${done.whatsapp.error}).`}
              </p>
            )}
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                تم
              </button>
            </div>
          </div>
        ) : (
          <AccountCreatedCard
            name={done.name}
            phone={done.phone}
            whatsapp={done.whatsapp}
            onClose={onClose}
          />
        )
      ) : (
        <form className="form" onSubmit={submit}>
          <ErrorNote error={error} />
          {!fixedRole && !joinRequest && (
            <Field
              id="u-role"
              label="الدور"
              hint={picked.profileId ? "بالصلاحيات المحددة له في «الأدوار والصلاحيات»" : undefined}
            >
              <select
                id="u-role"
                value={form.choice}
                onChange={(e) => setForm({ ...form, choice: e.target.value, circleId: circle?.id ?? "" })}
                required
              >
                <option value="">اختر الدور</option>
                {choices.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {staff && role !== "org_admin" && (
            <Field id="u-scope" label="النطاق">
              <div className="chips" role="radiogroup">
                <label className="check">
                  <input type="radio" checked={form.wide} onChange={() => setForm({ ...form, wide: true })} />
                  الجمعية كلها (كل المجمعات)
                </label>
                <label className="check">
                  <input
                    type="radio"
                    checked={!form.wide}
                    onChange={() => setForm({ ...form, wide: false })}
                  />
                  مجمع واحد
                </label>
              </div>
            </Field>
          )}
          <div className="form-row">
            <Field id="u-name" label="الاسم الكامل">
              <input
                id="u-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Field>
            <Field id="u-gender" label="الجنس" hint="يلزم للحلقات المخصصة لجنس واحد">
              <select
                id="u-gender"
                value={form.gender}
                onChange={(e) =>
                  setForm({ ...form, gender: e.target.value as Gender | "", circleId: circle?.id ?? "" })
                }
                disabled={!!joinRequest}
              >
                <option value="">غير محدد</option>
                <option value="male">{personGenderLabel.male}</option>
                <option value="female">{personGenderLabel.female}</option>
              </select>
            </Field>
          </div>
          <Field
            id="u-phone"
            label="رقم الجوال"
            hint="يدخل به المستخدم برمز يصله على الواتساب، ثم يختار كلمة مرور"
          >
            <PhoneField
              id="u-phone"
              value={form.phone}
              onChange={(phone) => setForm({ ...form, phone })}
              required
            />
          </Field>
          {!circle && !orgWide && (
            <Field id="u-branch" label="المجمع">
              <select
                id="u-branch"
                value={form.branchId}
                onChange={(e) => setForm({ ...form, branchId: e.target.value, circleId: "" })}
                disabled={!!joinRequest?.branchId}
                required
              >
                <option value="">اختر المجمع</option>
                {(branches.data?.branches ?? [])
                  .filter((b) => !b.archivedAt)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          {isStudent && !circle && (
            <Field
              id="u-circle"
              label="الحلقة"
              hint={form.gender ? undefined : "اختر الجنس لعرض الحلقات المخصصة لجنس واحد"}
            >
              <select
                id="u-circle"
                value={form.circleId}
                onChange={(e) => setForm({ ...form, circleId: e.target.value })}
                required
              >
                <option value="">اختر الحلقة</option>
                {circleChoices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.gender ? ` (${genderLabel[c.gender]})` : ""} — {c.studentCount}
                    {c.capacity ? `/${c.capacity}` : ""} طالب
                  </option>
                ))}
              </select>
            </Field>
          )}
          {circle && (
            <p className="muted small">
              سيُسجَّل في: <b>{circle.name}</b> · {circle.branchName}
            </p>
          )}
          {isStudent && (
            <>
              <ProgramFields
                value={program}
                onChange={setProgram}
                reviewMultiple={org?.settings.registration.reviewMultiple ?? 3}
              />
              <Field id="u-plan-from" label="تبدأ خطته من" hint="أول يوم يظهر فيه ورده">
                <input
                  id="u-plan-from"
                  type="date"
                  value={planFrom}
                  onChange={(e) => setPlanFrom(e.target.value)}
                />
              </Field>
            </>
          )}
          {role === "teacher" && (
            <p className="notice small">
              يُمنح الدور على مستوى المجمع المختار. إسناد المعلم إلى الحلقات يتم من صفحة الحلقة.
            </p>
          )}
          <div className="form-actions">
            <button className="btn btn-primary" disabled={busy || !valid}>
              {busy ? "جارٍ الإنشاء…" : joinRequest ? "قبول وإنشاء الحساب" : "إنشاء الحساب"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** After creating an account: how the person signs in (no password is handed over, ADR 0019). */
function AccountCreatedCard({
  name,
  phone,
  whatsapp,
  onClose,
}: {
  name: string;
  phone: string;
  /** Staff accounts get a WhatsApp welcome from the organization's number. */
  whatsapp?: CreateUserResponse["whatsapp"];
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // Staff sign in on the portal (this site); teachers and students in the app.
  const where = whatsapp ? `منصة مُبين: ${window.location.origin}/login` : "تطبيق مُبين";
  const text = `حياك الله ${name}\nأُنشئ حسابك في ${where}\nادخل برقم جوالك ${phone} واختر «رمز عبر الواتساب»، ثم اختر كلمة مرور.`;
  return (
    <div className="form">
      <p>
        أُنشئ حساب <b>{name}</b>. يدخل برقم جواله <span className="ltr">{phone}</span> برمز يصله على الواتساب،
        ثم يختار كلمة مروره بنفسه.
      </p>
      {whatsapp &&
        (whatsapp.sent ? (
          <p className="notice small" role="status">
            أُرسلت له رسالة واتساب من رقم الجمعية برابط المنصة وطريقة الدخول.
          </p>
        ) : (
          <p className="notice small" role="status">
            لم تُرسل رسالة الواتساب ({whatsapp.error}). انسخ رسالة الترحيب وأرسلها له.
          </p>
        ))}
      <div className="form-actions">
        <button
          className="btn btn-secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true));
          }}
        >
          <Copy size={16} strokeWidth={1.75} /> {copied ? "تم النسخ" : "نسخ رسالة الترحيب"}
        </button>
        <button className="btn btn-primary" onClick={onClose}>
          تم
        </button>
      </div>
    </div>
  );
}

/** Shown once after an administrator sets someone's password. */
export function CredentialsCard({ creds, onClose }: { creds: Credentials; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = `مُبين\nالاسم: ${creds.name}\nرقم الجوال: ${creds.login}\nكلمة المرور: ${creds.password}`;
  return (
    <div className="form">
      <p>سلّم بيانات الدخول للمستخدم. كلمة المرور لا تُحفظ ولا تُعرض مرة أخرى.</p>
      <dl className="kv" style={{ padding: 0 }}>
        <dt>الاسم</dt>
        <dd>{creds.name}</dd>
        <dt>رقم الجوال</dt>
        <dd>
          <span className="secret">{creds.login}</span>
        </dd>
        <dt>كلمة المرور</dt>
        <dd>
          <span className="secret">{creds.password}</span>
        </dd>
      </dl>
      <div className="form-actions">
        <button
          className="btn btn-secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true));
          }}
        >
          <Copy size={16} strokeWidth={1.75} /> {copied ? "تم النسخ" : "نسخ البيانات"}
        </button>
        <button className="btn btn-primary" onClick={onClose}>
          تم
        </button>
      </div>
    </div>
  );
}
